import React, { useState } from 'react';
import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Divider,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { PageHeader } from '../Components/ui';

const API = 'https://nobnohungry.de/api/users';

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);


const OAuthButton = ({ onClick, disabled, icon, label, dark = false }) => (
  <Button
    fullWidth
    variant={dark ? 'contained' : 'outlined'}
    onClick={onClick}
    disabled={disabled}
    sx={{
      py: 1.5,
      fontSize: '1rem',
      fontWeight: 500,
      borderColor: dark ? undefined : 'divider',
      color: dark ? 'common.white' : 'text.primary',
      backgroundColor: dark ? 'grey.900' : 'background.paper',
      '&:hover': {
        backgroundColor: dark ? 'grey.800' : 'action.hover',
        borderColor: dark ? undefined : 'text.secondary',
      },
      '&.Mui-disabled': {
        opacity: 0.6,
      },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      {icon}
      {label}
    </Box>
  </Button>
);

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const saveTokenAndRedirect = (token) => {
    localStorage.setItem('token', token);
    const pendingInviteCode = localStorage.getItem('pendingInviteCode');
    if (pendingInviteCode) {
      localStorage.removeItem('pendingInviteCode');
      navigate(`/invite/${pendingInviteCode}`);
    } else {
      navigate('/dashboard');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (res.ok) {
        const data = await res.json();
        saveTokenAndRedirect(data.token);
      } else {
        setError('E-Mail oder Passwort falsch. Bitte überprüfe deine Angaben.');
      }
    } catch {
      setError('Verbindungsfehler. Bitte versuche es erneut.');
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`${API}/auth/google`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ accessToken: tokenResponse.access_token }),
        });
        if (res.ok) {
          const data = await res.json();
          saveTokenAndRedirect(data.token);
        } else {
          setError('Google-Login fehlgeschlagen. Bitte versuche es erneut.');
        }
      } catch {
        setError('Verbindungsfehler beim Google-Login.');
      } finally {
        setLoading(false);
      }
    },
    onError: () => setError('Google-Login wurde abgebrochen.'),
  });

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        px: 3,
        pb: 10, // Platz für die Bottom Navigation
        maxWidth: 440,
        mx: 'auto',
      }}
    >
      {/* App-Titel */}
      <PageHeader variant="auth" title="🔥 NoBNoHungry" subtitle="Dein smarter Kalorienzähler" />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* OAuth-Buttons */}
      <OAuthButton
        onClick={() => googleLogin()}
        disabled={loading}
        icon={<GoogleIcon />}
        label="Mit Google anmelden"
      />

      <Divider sx={{ my: 3 }}>
        <Typography variant="body2" color="text.secondary">
          oder
        </Typography>
      </Divider>

      {/* E-Mail / Passwort */}
      <Box component="form" onSubmit={handleLogin} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <TextField
          label="E-Mail Adresse"
          type="email"
          variant="outlined"
          fullWidth
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          inputProps={{ autoCapitalize: 'none', autoCorrect: 'off' }}
        />
        <TextField
          label="Passwort"
          type="password"
          variant="outlined"
          fullWidth
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={loading}
          sx={{ mt: 1, py: 1.5, fontSize: '1rem' }}
        >
          {loading ? <CircularProgress size={22} color="inherit" /> : 'Anmelden'}
        </Button>
      </Box>

      <Button
        variant="text"
        fullWidth
        sx={{ mt: 2, color: 'text.secondary' }}
        onClick={() => navigate('/register')}
      >
        Noch kein Konto? Jetzt registrieren
      </Button>
    </Box>
  );
};

export default Login;
